import { motion } from "framer-motion";
import { founder, director, timeline } from "../data/site";

const rev = {
    hidden: { opacity: 0, y: 30 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, delay: i * 0.08, ease: [0.2, 0.7, 0.2, 1] },
    }),
};

function Portrait({ p, tag, idx }) {
    return (
        <motion.figure
            initial={{ opacity: 0, scale: 1.06, y: 20 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 1.1, delay: idx * 0.12, ease: [0.2, 0.7, 0.2, 1] }}
            className="relative"
        >
            <div className="relative aspect-[4/5] overflow-hidden">
                <img
                    src={p.img}
                    alt={p.name}
                    className={`w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-105 ${
                        idx === 0 ? "grayscale contrast-125" : ""
                    }`}
                />
                <div className="absolute inset-0 border border-[rgba(212,175,55,0.35)]" />
                <div className="absolute top-4 left-4 right-4 flex justify-between text-[9px] uppercase tracking-[0.35em] text-[#E2C56D]">
                    <span>{tag.code}</span>
                    <span>{tag.year}</span>
                </div>
            </div>
            <figcaption className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.4em] text-[#C89B3C]">
                    {tag.label}
                </div>
                <div className="mt-2 font-display text-xl md:text-2xl text-white leading-tight">
                    {p.name}
                </div>
                <div className="text-sm text-[#bebebe] mt-1">{p.role}</div>
            </figcaption>
        </motion.figure>
    );
}

export default function Legacy() {
    return (
        <section
            id="legacy"
            className="relative bg-[#0b0b0b] py-24 md:py-40 px-6 md:px-10 overflow-hidden"
            data-testid="legacy-section"
        >
            <div className="mx-auto max-w-[1440px]">
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-15%" }}
                    variants={rev}
                    className="flex items-center gap-6 mb-14"
                >
                    <span className="w-16 h-px bg-[#C89B3C]" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-[#C89B3C]">
                        Chapter 02 — Origins
                    </span>
                </motion.div>

                <div className="grid grid-cols-12 gap-6 md:gap-10 items-start">
                    <motion.h2
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-15%" }}
                        variants={rev}
                        className="col-span-12 lg:col-span-7 font-display font-light text-white text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
                        data-testid="legacy-heading"
                    >
                        A Legacy <em className="text-[#E2C56D] not-italic">Built</em> on
                        <br />
                        <span className="italic text-[#E2C56D]">Vision.</span>
                    </motion.h2>

                    <motion.p
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-15%" }}
                        variants={rev}
                        custom={1}
                        className="col-span-12 lg:col-span-5 text-[#bebebe] text-base md:text-lg leading-relaxed font-light lg:pt-4"
                    >
                        Three generations. One enduring standard — quiet,
                        deliberate, and still unfolding across Bangalore.
                    </motion.p>
                </div>

                {/* Portraits — side by side */}
                <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-16">
                    <Portrait
                        p={founder}
                        idx={0}
                        tag={{ code: "FR — 01", year: "1984", label: "The Founder" }}
                    />
                    <Portrait
                        p={director}
                        idx={1}
                        tag={{ code: "DR — 02", year: "PRESENT", label: "The Director" }}
                    />
                </div>

                {/* Common description */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-15%" }}
                    transition={{ duration: 0.9, delay: 0.15 }}
                    className="mt-16 md:mt-24 grid grid-cols-12 gap-6 md:gap-10"
                >
                    <div className="col-span-12 lg:col-span-2 flex lg:flex-col items-start lg:items-start gap-3">
                        <span className="w-16 h-px bg-[#C89B3C]" />
                        <span className="text-[10px] uppercase tracking-[0.4em] text-[#C89B3C]">
                            The Story
                        </span>
                    </div>
                    <div className="col-span-12 lg:col-span-10 lg:pr-8">
                        <p className="text-[#e6e6e6] text-xl md:text-2xl leading-relaxed font-light font-serif-alt">
                            Founded in <span className="text-[#E2C56D]">1984</span> by Late Dr.
                            B. R. Ahmedulla Sheriff, BRA Sheriff Builders &amp; Developers was
                            established with a simple vision — to create quality homes and
                            developments built on{" "}
                            <em className="text-[#E2C56D] not-italic">trust, integrity, and
                            lasting value.</em>
                        </p>
                        <p className="mt-6 text-[#bebebe] text-base md:text-lg leading-relaxed font-light max-w-4xl">
                            Today, the company is led by his son{" "}
                            <span className="text-white">Mr. B. A. Usman Sheriff</span>, Project
                            Executive &amp; Director — carrying forward the same principles
                            while embracing modern construction techniques, innovative planning
                            and customer-focused development. Every project is drawn, poured
                            and delivered by a family that treats every square foot as its own
                            signature.
                        </p>
                    </div>
                </motion.div>

                {/* Timeline */}
                <div className="mt-20 md:mt-28">
                    <div className="text-[10px] uppercase tracking-[0.4em] text-[#C89B3C] mb-8">
                        A Timeline
                    </div>
                    <div className="relative">
                        <div className="absolute left-0 right-0 top-6 h-px bg-[rgba(212,175,55,0.22)]" />
                        <motion.div
                            initial={{ scaleX: 0 }}
                            whileInView={{ scaleX: 1 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 1.6, ease: [0.2, 0.7, 0.2, 1] }}
                            style={{ transformOrigin: "left" }}
                            className="absolute left-0 top-6 h-px bg-[#C89B3C] w-full"
                        />
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
                            {timeline.map((t, i) => (
                                <motion.div
                                    key={t.year}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-15%" }}
                                    transition={{ duration: 0.7, delay: 0.2 + i * 0.12 }}
                                    className="relative pt-10"
                                    data-testid={`timeline-${i}`}
                                >
                                    <span className="absolute top-[18px] left-0 w-3 h-3 border border-[#C89B3C] bg-[#0b0b0b] -translate-y-1/2" />
                                    <div className="font-display text-3xl md:text-4xl text-[#E2C56D]">
                                        {t.year}
                                    </div>
                                    <div className="mt-2 text-[11px] uppercase tracking-[0.25em] text-[#bebebe] leading-relaxed">
                                        {t.label}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
